import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  GripVertical,
  Trash2,
  Copy,
  Check,
  Eye,
  X,
  Type,
  Mail,
  Phone,
  AlignLeft,
  ChevronDown,
  Upload,
  CheckSquare,
  Save,
  ImagePlus,
  Palette,
  FileText as ResumeIcon,
  Video as VideoIcon,
  User,
} from 'lucide-react';
import { useForm, useCreateForm, useUpdateForm, useUploadFormLogo, useUploadFormBackground } from '../hooks/useApi';
import { cn } from '../lib/utils';
import type { FormField } from '../types';

// Simple toggle switch
const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="flex items-center gap-3 cursor-pointer"
  >
    <div className={cn(
      "relative w-11 h-6 rounded-full transition-colors",
      checked ? "bg-primary-600" : "bg-gray-200"
    )}>
      <div className={cn(
        "absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform",
        checked && "translate-x-5"
      )} />
    </div>
    <span className="text-sm font-medium text-gray-700">{label}</span>
  </button>
);

const FIELD_TYPES: { type: FormField['type']; label: string; icon: React.ElementType; preset?: Partial<FormField> }[] = [
  { type: 'text', label: 'First Name', icon: User, preset: { label: 'First Name', required: true } },
  { type: 'text', label: 'Last Name', icon: User, preset: { label: 'Last Name', required: true } },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'email', label: 'Email', icon: Mail, preset: { label: 'Email', required: true } },
  { type: 'phone', label: 'Phone', icon: Phone, preset: { label: 'Phone', required: true } },
  { type: 'textarea', label: 'Textarea', icon: AlignLeft },
  { type: 'select', label: 'Dropdown', icon: ChevronDown },
  { type: 'file', label: 'Resume', icon: ResumeIcon, preset: { label: 'Resume', accept: '.pdf,.doc,.docx,.txt,.rtf', maxFileSize: 10, required: true } },
  { type: 'file', label: 'Cover Letter', icon: ResumeIcon, preset: { label: 'Cover Letter', accept: '.pdf,.doc,.docx,.txt,.rtf', maxFileSize: 10 } },
  { type: 'file', label: 'Video', icon: VideoIcon, preset: { label: 'Video Introduction', accept: 'video/*', maxFileSize: 50 } },
  { type: 'file', label: 'File Upload', icon: Upload },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

const ACCEPT_OPTIONS: { value: string; label: string }[] = [
  { value: '.pdf,.doc,.docx,.txt,.rtf', label: 'Documents' },
  { value: 'image/*', label: 'Images' },
  { value: 'video/*', label: 'Videos' },
  { value: 'image/*,video/*,.pdf,.doc,.docx,.txt,.rtf', label: 'Images + Documents' },
  { value: '*', label: 'Any' },
];

function generateId() {
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export default function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const { data: formData, isLoading } = useForm(id || '');
  const createForm = useCreateForm();
  const updateForm = useUpdateForm();
  const uploadFormLogo = useUploadFormLogo();
  const uploadFormBackground = useUploadFormBackground();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  // Forms are always active. Delete the form instead of deactivating it.
  const status = 'active' as const;
  const [fields, setFields] = useState<FormField[]>(() => {
    // Default fields for new forms
    if (!id) {
      return [
        { id: generateId(), type: 'text' as const, label: 'First Name', required: true, order: 0 },
        { id: generateId(), type: 'text' as const, label: 'Last Name', required: true, order: 1 },
        { id: generateId(), type: 'email' as const, label: 'Email', required: true, order: 2 },
        { id: generateId(), type: 'phone' as const, label: 'Phone', required: true, order: 3 },
        { id: generateId(), type: 'file' as const, label: 'Resume', required: true, accept: '.pdf,.doc,.docx,.txt,.rtf' as const, maxFileSize: 10, order: 4 },
      ];
    }
    return [];
  });
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedUrl, setSavedUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showSavedToast, setShowSavedToast] = useState(false);

  // Branding state
  const [companyNameOverride, setCompanyNameOverride] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#f8fafc');
  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [bgImagePreview, setBgImagePreview] = useState<string | null>(null);
  const [bgImageFile, setBgImageFile] = useState<File | null>(null);
  const [bgImageRemoved, setBgImageRemoved] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);

  // Auto-invite state (thank you is always sent, these control interview invites)
  const [autoInviteSms, setAutoInviteSms] = useState(false);
  const [autoInviteSmsMessage, setAutoInviteSmsMessage] = useState(
    "Hi {{firstName}}, you're invited to interview for {{jobTitle}}! Please reply with your availability or call us to schedule."
  );
  const [autoInviteEmail, setAutoInviteEmail] = useState(false);
  const [autoInviteEmailSubject, setAutoInviteEmailSubject] = useState('Interview Invitation - {{jobTitle}}');
  const [autoInviteEmailBody, setAutoInviteEmailBody] = useState(
    "Hi {{firstName}},\n\nWe were impressed by your application for {{jobTitle}} at {{companyName}}.\n\nWe'd love to invite you to an interview! Please reply to this email or call us to schedule a time that works for you.\n\nWe look forward to speaking with you!\n\nBest regards,\n{{companyName}}"
  );

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const dragCounter = useRef(0);

  // Load form data in edit mode
  useEffect(() => {
    if (formData?.form) {
      setName(formData.form.name);
      setDescription(formData.form.description || '');
      setJobTitle(formData.form.job_title);
      // status intentionally not loaded — forms are always 'active', delete instead of deactivating

      // fields may arrive as a JSON string from the API
      const rawFields = formData.form.fields;
      const parsedFields = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
      setFields(Array.isArray(parsedFields) ? parsedFields : []);

      // Load branding settings
      const rawSettings = formData.form.settings;
      const settings = typeof rawSettings === 'string' ? JSON.parse(rawSettings) : rawSettings;
      if (settings) {
        setCompanyNameOverride(settings.company_name || '');
        if (settings.background_color) setBackgroundColor(settings.background_color);
        if (settings.primary_color) setPrimaryColor(settings.primary_color);
        if (settings.logo_key && !logoRemoved) {
          setLogoPreview(`https://api.ihirefast.com/forms/public/assets/${formData.form.id}/logo?t=${Date.now()}`);
        } else if (!settings.logo_key) {
          setLogoPreview(null);
        }
        if (settings.background_key && !bgImageRemoved) {
          setBgImagePreview(`https://api.ihirefast.com/forms/public/assets/${formData.form.id}/background?t=${Date.now()}`);
        } else if (!settings.background_key) {
          setBgImagePreview(null);
        }
        // Load auto-invite settings
        if (settings.auto_invite_sms) setAutoInviteSms(true);
        if (settings.auto_invite_sms_message) setAutoInviteSmsMessage(settings.auto_invite_sms_message);
        if (settings.auto_invite_email) setAutoInviteEmail(true);
        if (settings.auto_invite_email_subject) setAutoInviteEmailSubject(settings.auto_invite_email_subject);
        if (settings.auto_invite_email_body) setAutoInviteEmailBody(settings.auto_invite_email_body);
      } else {
        setCompanyNameOverride('');
        setLogoPreview(null);
        setBgImagePreview(null);
      }

      setSavedUrl(formData.form.public_url || null);
    }
  }, [formData]);

  const addField = useCallback((type: FormField['type'], preset?: Partial<FormField>) => {
    const newField: FormField = {
      id: generateId(),
      type,
      label: preset?.label || '',
      required: preset?.required || false,
      order: fields.length,
      ...(type === 'select' ? { options: [''] } : {}),
      ...(type === 'file' ? { accept: preset?.accept || '*', maxFileSize: preset?.maxFileSize || 50 } : {}),
    };
    setFields((prev) => [...prev, newField]);
  }, [fields.length]);

  const updateField = useCallback((id: string, updates: Partial<FormField>) => {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  }, []);

  const removeField = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id).map((f, i) => ({ ...f, order: i })));
  }, []);

  const addOption = useCallback((fieldId: string) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId ? { ...f, options: [...(f.options || []), ''] } : f
      )
    );
  }, []);

  const updateOption = useCallback((fieldId: string, optIndex: number, value: string) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? { ...f, options: (f.options || []).map((o, i) => (i === optIndex ? value : o)) }
          : f
      )
    );
  }, []);

  const removeOption = useCallback((fieldId: string, optIndex: number) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? { ...f, options: (f.options || []).filter((_, i) => i !== optIndex) }
          : f
      )
    );
  }, []);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Make the drag image slightly transparent
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDragIndex(null);
    setDropIndex(null);
    dragCounter.current = 0;
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragCounter.current++;
    setDropIndex(index);
  };

  const handleDragLeave = () => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDropIndex(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    dragCounter.current = 0;
    const sourceIndex = dragIndex;
    if (sourceIndex === null || sourceIndex === targetIndex) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }

    setFields((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(sourceIndex, 1);
      updated.splice(targetIndex, 0, moved);
      return updated.map((f, i) => ({ ...f, order: i }));
    });

    setDragIndex(null);
    setDropIndex(null);
  };

  const handleSave = async () => {
    if (!name.trim() || !jobTitle.trim()) return;

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        job_title: jobTitle.trim(),
        status,
        fields: fields.map((f, i) => ({ ...f, order: i })),
        settings: {
          company_name: companyNameOverride.trim() || undefined,
          background_color: backgroundColor !== '#f8fafc' ? backgroundColor : undefined,
          primary_color: primaryColor !== '#2563eb' ? primaryColor : undefined,
          ...(logoRemoved ? { logo_key: '__REMOVE__' } : {}),
          ...(bgImageRemoved ? { background_key: '__REMOVE__' } : {}),
          auto_invite_sms: autoInviteSms || undefined,
          auto_invite_sms_message: autoInviteSms ? autoInviteSmsMessage : undefined,
          auto_invite_email: autoInviteEmail || undefined,
          auto_invite_email_subject: autoInviteEmail ? autoInviteEmailSubject : undefined,
          auto_invite_email_body: autoInviteEmail ? autoInviteEmailBody : undefined,
        },
      };

      let result;
      if (isEditMode && id) {
        result = await updateForm.mutateAsync({ id, ...payload });
      } else {
        result = await createForm.mutateAsync(payload);
      }

      if (result?.form?.public_url) {
        setSavedUrl(result.form.public_url);
      }

      // Upload logo and background image if pending
      const formId = result?.form?.id || id;
      if (formId) {
        if (logoFile) {
          await uploadFormLogo.mutateAsync({ id: formId, file: logoFile });
          setLogoPreview(`https://api.ihirefast.com/forms/public/assets/${formId}/logo?t=${Date.now()}`);
          setLogoFile(null);
        }
        if (bgImageFile) {
          await uploadFormBackground.mutateAsync({ id: formId, file: bgImageFile });
          setBgImagePreview(`https://api.ihirefast.com/forms/public/assets/${formId}/background?t=${Date.now()}`);
          setBgImageFile(null);
        }
      }

      // Reset remove flags after save
      setLogoRemoved(false);
      setBgImageRemoved(false);

      // Show saved toast
      setShowSavedToast(true);
      setTimeout(() => setShowSavedToast(false), 3000);

      if (!isEditMode && result?.form?.id) {
        navigate(`/forms/${result.form.id}`, { replace: true });
      }
    } catch (err) {
      console.error('Save error:', err);
      alert(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    if (!savedUrl) return;
    navigator.clipboard.writeText(savedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getAcceptLabel = (accept?: string) => {
    const opt = ACCEPT_OPTIONS.find((o) => o.value === accept);
    return opt?.label || 'Any';
  };

  if (isEditMode && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/forms')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditMode ? 'Edit Form' : 'Create Form'}
            </h2>
            <p className="text-sm text-gray-500">
              {isEditMode ? 'Update your application form' : 'Build a custom application form'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreview(true)}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || !jobTitle.trim()}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Saved URL */}
      {savedUrl && (
        <div className="card p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-green-800">Public URL:</span>
            <input
              type="text"
              value={savedUrl}
              readOnly
              className="flex-1 text-sm bg-white border border-green-200 rounded px-3 py-1.5 text-gray-700"
            />
            <button
              onClick={copyUrl}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-200 rounded hover:bg-green-50"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar: Field Types */}
        <div className="lg:col-span-1 order-1 lg:order-1">
          <div className="card p-4 sticky top-24">
            <h3 className="font-medium text-gray-900 mb-3">Add Fields</h3>
            <div className="space-y-2">
              {FIELD_TYPES.map((ft, idx) => {
                const Icon = ft.icon;
                // Only allow one First Name and one Last Name
                const singleUseLabels = ['First Name', 'Last Name'];
                const isDisabled = singleUseLabels.includes(ft.label) && fields.some(f => f.label === ft.label);
                return (
                  <button
                    key={`${ft.type}-${ft.label}-${idx}`}
                    onClick={() => !isDisabled && addField(ft.type, ft.preset)}
                    disabled={isDisabled}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg border transition-colors",
                      isDisabled
                        ? "text-gray-300 border-gray-100 cursor-not-allowed bg-gray-50"
                        : "text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-primary-300"
                    )}
                  >
                    <Icon className={cn("w-4 h-4", isDisabled ? "text-gray-200" : "text-gray-400")} />
                    {ft.label}
                    {isDisabled ? (
                      <Check className="w-3.5 h-3.5 text-green-400 ml-auto" />
                    ) : (
                      <Plus className="w-3.5 h-3.5 text-gray-300 ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main: Form Config + Fields */}
        <div className="lg:col-span-3 order-2 lg:order-2 space-y-6">
          {/* Form Details */}
          <div className="card p-4 lg:p-6 space-y-4">
            <h3 className="font-medium text-gray-900">Form Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  placeholder="e.g. Warehouse Associate Application"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input resize-none"
                  rows={2}
                  placeholder="Brief description of the form"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className="input"
                  placeholder="e.g. Warehouse Associate"
                />
              </div>
            </div>
          </div>

          {/* Branding & Appearance */}
          <div className="card p-4 lg:p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-gray-400" />
              <h3 className="font-medium text-gray-900">Branding & Appearance</h3>
            </div>

            {/* Company Name Override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name (shown on form)
              </label>
              <input
                type="text"
                value={companyNameOverride}
                onChange={(e) => setCompanyNameOverride(e.target.value)}
                className="input"
                placeholder="e.g. Acme Corp"
              />
              <p className="text-xs text-gray-400 mt-1">
                Leave empty to use your organization name from Settings
              </p>
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
              {logoPreview ? (
                <div className="flex items-center gap-4">
                  <img
                    src={logoPreview}
                    alt="Form logo"
                    className="w-16 h-16 rounded-lg object-contain border border-gray-200 bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setLogoPreview(null);
                      setLogoFile(null);
                      setLogoRemoved(true);
                      if (logoInputRef.current) logoInputRef.current.value = '';
                    }}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-primary-300 hover:bg-gray-50 transition-colors"
                >
                  <ImagePlus className="w-4 h-4" />
                  Upload Logo
                </button>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) {
                    alert('Logo must be under 2MB');
                    return;
                  }
                  setLogoFile(file);
                  setLogoPreview(URL.createObjectURL(file));
                }}
              />
              {!isEditMode && !id && (
                <p className="text-xs text-amber-500 mt-1">
                  Save the form first to upload a logo.
                </p>
              )}
            </div>

            {/* Colors */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Background Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-10 h-10 rounded border border-gray-200 cursor-pointer p-0.5"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setBackgroundColor(v);
                    }}
                    className="input w-28 font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-10 h-10 rounded border border-gray-200 cursor-pointer p-0.5"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setPrimaryColor(v);
                    }}
                    className="input w-28 font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            {/* Background Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Background Image <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              {bgImagePreview ? (
                <div className="flex items-center gap-4">
                  <img
                    src={bgImagePreview}
                    alt="Background"
                    className="w-24 h-16 rounded-lg object-cover border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setBgImagePreview(null);
                      setBgImageFile(null);
                      setBgImageRemoved(true);
                      if (bgInputRef.current) bgInputRef.current.value = '';
                    }}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => bgInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-primary-300 hover:bg-gray-50 transition-colors"
                >
                  <ImagePlus className="w-4 h-4" />
                  Upload Background Image
                </button>
              )}
              <input
                ref={bgInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) {
                    alert('Background image must be under 5MB');
                    return;
                  }
                  setBgImageFile(file);
                  setBgImagePreview(URL.createObjectURL(file));
                }}
              />
              {!isEditMode && !id && (
                <p className="text-xs text-amber-500 mt-1">
                  Save the form first to upload a background image.
                </p>
              )}
            </div>
          </div>

          {/* Auto-Responses & Scheduling */}
          <div className="card p-4 lg:p-6 space-y-5">
            <h3 className="font-medium text-gray-900">Auto-Responses & Scheduling</h3>

            {/* Info box: thank you is always sent */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                A thank you message is automatically sent via SMS and email to every applicant. You cannot disable this.
              </p>
            </div>

            <div className="border-t border-gray-100" />

            {/* Auto-send interview invite via SMS */}
            <div className="space-y-3">
              <Toggle
                checked={autoInviteSms}
                onChange={setAutoInviteSms}
                label="Auto-send interview invite via SMS"
              />
              {autoInviteSms && (
                <div className="ml-14 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Invite Message Template
                  </label>
                  <textarea
                    value={autoInviteSmsMessage}
                    onChange={(e) => setAutoInviteSmsMessage(e.target.value)}
                    className="input resize-none"
                    rows={3}
                    placeholder="Hi {{firstName}}, you're invited to interview for {{jobTitle}}!"
                  />
                  <p className="text-xs text-gray-400">
                    Available variables: <code className="bg-gray-100 px-1 rounded">{'{{firstName}}'}</code>{' '}
                    <code className="bg-gray-100 px-1 rounded">{'{{jobTitle}}'}</code>{' '}
                    <code className="bg-gray-100 px-1 rounded">{'{{companyName}}'}</code>
                  </p>
                  <p className="text-xs text-amber-600">
                    Sent automatically after the thank you SMS. Requires phone number.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100" />

            {/* Auto-send interview invite via Email */}
            <div className="space-y-3">
              <Toggle
                checked={autoInviteEmail}
                onChange={setAutoInviteEmail}
                label="Auto-send interview invite via Email"
              />
              {autoInviteEmail && (
                <div className="ml-14 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={autoInviteEmailSubject}
                      onChange={(e) => setAutoInviteEmailSubject(e.target.value)}
                      className="input"
                      placeholder="Interview Invitation - {{jobTitle}}"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Body
                    </label>
                    <textarea
                      value={autoInviteEmailBody}
                      onChange={(e) => setAutoInviteEmailBody(e.target.value)}
                      className="input resize-none"
                      rows={6}
                      placeholder="Hi {{firstName}},&#10;&#10;We'd love to invite you to an interview..."
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Available variables: <code className="bg-gray-100 px-1 rounded">{'{{firstName}}'}</code>{' '}
                    <code className="bg-gray-100 px-1 rounded">{'{{jobTitle}}'}</code>{' '}
                    <code className="bg-gray-100 px-1 rounded">{'{{companyName}}'}</code>
                  </p>
                  <p className="text-xs text-amber-600">
                    Sent automatically after the thank you email. Requires email address.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Fields */}
          <div className="card p-4 lg:p-6">
            <h3 className="font-medium text-gray-900 mb-4">
              Form Fields ({fields.length})
            </h3>

            {fields.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <Type className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">
                  No fields added yet. Use the sidebar to add fields.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className={cn(
                      'border rounded-lg p-4 bg-white transition-all',
                      dragIndex === index && 'opacity-50',
                      dropIndex === index && dragIndex !== index && 'border-primary-400 ring-2 ring-primary-100',
                      dropIndex !== index && 'border-gray-200'
                    )}
                  >
                    {/* Drop indicator line */}
                    {dropIndex === index && dragIndex !== null && dragIndex !== index && (
                      <div className="h-0.5 bg-primary-500 rounded -mt-4 mb-3 -mx-4" />
                    )}

                    <div className="flex items-start gap-3">
                      {/* Drag Handle */}
                      <div className="pt-2 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500">
                        <GripVertical className="w-5 h-5" />
                      </div>

                      {/* Field Content */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateField(field.id, { label: e.target.value })}
                            className="flex-1 min-w-[200px] input"
                            placeholder="Field label"
                          />
                          <span
                            className={cn(
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                              field.type === 'email' ? 'bg-blue-100 text-blue-800' :
                              field.type === 'phone' ? 'bg-purple-100 text-purple-800' :
                              field.type === 'textarea' ? 'bg-orange-100 text-orange-800' :
                              field.type === 'select' ? 'bg-cyan-100 text-cyan-800' :
                              field.type === 'file' ? 'bg-pink-100 text-pink-800' :
                              field.type === 'checkbox' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            )}
                          >
                            {FIELD_TYPES.find((ft) => ft.type === field.type)?.label || field.type}
                          </span>
                        </div>

                        {/* File accept options */}
                        {field.type === 'file' && (
                          <div className="flex items-center gap-4 flex-wrap">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Accepted files</label>
                              <select
                                value={field.accept || '*'}
                                onChange={(e) =>
                                  updateField(field.id, { accept: e.target.value as FormField['accept'] })
                                }
                                className="input text-sm py-1.5"
                              >
                                {ACCEPT_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Max size (MB)</label>
                              <input
                                type="number"
                                value={field.maxFileSize || 10}
                                onChange={(e) =>
                                  updateField(field.id, { maxFileSize: parseInt(e.target.value) || 10 })
                                }
                                className="input text-sm py-1.5 w-24"
                                min={1}
                                max={100}
                              />
                            </div>
                            <p className="text-xs text-gray-400 self-end pb-1.5">
                              Accepts: {getAcceptLabel(field.accept)}
                            </p>
                          </div>
                        )}

                        {/* Select options */}
                        {field.type === 'select' && (
                          <div className="space-y-2">
                            <label className="block text-xs text-gray-500">Options</label>
                            {(field.options || []).map((opt, optIndex) => (
                              <div key={optIndex} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={opt}
                                  onChange={(e) => updateOption(field.id, optIndex, e.target.value)}
                                  className="input text-sm py-1.5 flex-1"
                                  placeholder={`Option ${optIndex + 1}`}
                                />
                                <button
                                  onClick={() => removeOption(field.id, optIndex)}
                                  className="p-1 text-gray-300 hover:text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addOption(field.id)}
                              className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Add option
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(field.id, { required: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-xs text-gray-500">Required</span>
                        </label>
                        <button
                          onClick={() => removeField(field.id)}
                          className="p-1.5 text-gray-300 hover:text-red-500 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowPreview(false)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Form Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <h4 className="text-xl font-bold text-gray-900">{name || 'Untitled Form'}</h4>
              {description && <p className="text-gray-600 mt-1">{description}</p>}
              {jobTitle && (
                <p className="text-sm text-primary-600 mt-2 font-medium">{jobTitle}</p>
              )}
            </div>

            <div className="space-y-5">
              {fields.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No fields added</p>
              ) : (
                fields.map((field) => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label || 'Untitled Field'}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {field.type === 'text' && (
                      <input type="text" disabled className="input bg-gray-50" placeholder={field.placeholder} />
                    )}
                    {field.type === 'email' && (
                      <input type="email" disabled className="input bg-gray-50" placeholder="email@example.com" />
                    )}
                    {field.type === 'phone' && (
                      <input type="tel" disabled className="input bg-gray-50" placeholder="+1 (555) 000-0000" />
                    )}
                    {field.type === 'textarea' && (
                      <textarea disabled className="input bg-gray-50 resize-none" rows={3} placeholder={field.placeholder} />
                    )}
                    {field.type === 'select' && (
                      <select disabled className="input bg-gray-50">
                        <option value="">Select...</option>
                        {(field.options || []).filter(Boolean).map((opt, i) => (
                          <option key={i}>{opt}</option>
                        ))}
                      </select>
                    )}
                    {field.type === 'file' && (
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                        <Upload className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          {getAcceptLabel(field.accept)} - Max {field.maxFileSize || 10}MB
                        </p>
                      </div>
                    )}
                    {field.type === 'checkbox' && (
                      <label className="flex items-center gap-2">
                        <input type="checkbox" disabled className="rounded border-gray-300" />
                        <span className="text-sm text-gray-600">{field.label || 'Checkbox'}</span>
                      </label>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="mt-8">
              <button disabled className="btn-primary w-full opacity-75">
                Submit Application
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Toast */}
      {showSavedToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
          <Check className="w-5 h-5" />
          <span className="font-medium">Changes saved!</span>
        </div>
      )}
    </div>
  );
}
